import { ButtonHTMLAttributes, FC } from 'react';
import styled from 'styled-components';
import { lightTheme } from '../../styles/theme';

type ButtonColor =
	| 'primary'
	| 'secondary'
	| 'success'
	| 'danger'
	| 'warning'
	| 'neutral'
	| 'black';
type ButtonVariant = 'fill' | 'outline' | 'ghost';

type ButtonProps = {
	text: string;
	variant?: ButtonVariant;
	color?: ButtonColor;
	fullWidth?: boolean;
} & ButtonHTMLAttributes<HTMLButtonElement>;

export const Button: FC<ButtonProps> = ({
	text,
	variant = 'fill',
	color = 'primary',
	fullWidth = false,
	...props
}) => {
	if (variant === 'outline') {
		return (
			<GhostButton fullWidth={fullWidth} outline color={color} {...props}>
				{text}
			</GhostButton>
		);
	}
	if (variant === 'ghost') {
		return (
			<GhostButton
				fullWidth={fullWidth}
				outline={false}
				color={color}
				{...props}
			>
				{text}
			</GhostButton>
		);
	}

	return (
		<FilledButton fullWidth={fullWidth} color={color} {...props}>
			{text}
		</FilledButton>
	);
};

const DefaultButton = styled.button<{
	fullWidth: boolean;
}>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: ${({ fullWidth }) => (fullWidth ? '100%' : 'fit-content')};
	height: 40px;
	padding: 8px 16px;
	cursor: pointer;
	border: none;
	border-radius: 8px;
	font-size: 14px;
	font-weight: normal;
	line-height: 24px;
	transition: 0.1s;
	text-wrap: nowrap;
`;

const GhostButton = styled(DefaultButton)<{
	color: ButtonColor;
	outline: boolean;
}>`
	background: ${lightTheme.buttons.outline.background.primary};
	color: ${({ color }) => lightTheme.buttons.outline.text[color]};
	border: 1.5px solid ${({ color }) => lightTheme.buttons.outline.border[color]};
	border: ${({ outline }) => !outline && 'none'};

	&:hover {
		background: ${({ color }) =>
			lightTheme.buttons.outline.background[`${color}Hover`]};
	}
`;

const FilledButton = styled(DefaultButton)<{
	color: ButtonColor;
}>`
	background: ${({ color }) => lightTheme.buttons.fill.background[color]};
	color: ${({ color }) => {
		if (
			color === 'primary' ||
			color === 'success' ||
			color === 'danger' ||
			color === 'warning' ||
			color === 'black'
		) {
			return lightTheme.buttons.fill.text.light;
		}
		if (color === 'secondary' || color === 'neutral') {
			return lightTheme.buttons.fill.text.dark;
		}
	}};
	box-shadow: ${({ color }) =>
		color === 'neutral' &&
		`0px 4px 6px -1px rgba(0, 0, 0, 0.1),
		0px 2px 4px -1px rgba(0, 0, 0, 0.06);`};

	&:hover {
		background: ${({ color }) =>
			lightTheme.buttons.fill.background[`${color}Hover`]};
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`;
